import { useId, useState } from 'react';
import { ChevronRight, Trophy, Calendar, Tablet, Users, Trash2 } from 'lucide-react';
import { PORTAL_BRAND } from '../../data/brand';
import {
  TOURNAMENT_TYPES,
  ELO_LEVELS,
  banepadCourtList,
  banepadResolveCourt,
  clubEffectiveCourts,
  getTournamentWinners,
  getTournamentType,
} from '../../engine';
import { useLocale } from '../../i18n';
import { Dialog } from '../ui';
import { safeId } from '../ui/labelledControl';
import { DraftsCard } from './DraftsCard';
import { PausedTournamentsCard } from './PausedTournamentsCard';

// deviation: STARPOINT_ICON_URI was defined in legacy/App.jsx; copied verbatim as a
// module-local const (a public asset path string, not engine logic) to keep this
// component self-contained.
const STARPOINT_ICON_URI = '/starpoint-icon.png';

// The two delete confirmations' look, moved verbatim from their hand-built
// overlays onto `Dialog` (#900). Inline styles rather than classes, as they
// always were here.
const CONFIRM_BACKDROP_STYLE = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 9999,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 16,
  background: 'rgba(15, 22, 17, 0.55)',
} as const;
const CONFIRM_PANEL_STYLE = {
  background: '#ffffff',
  maxWidth: 420,
  width: '100%',
  borderRadius: 16,
  padding: 20,
  boxShadow: '0 25px 60px -10px rgba(0,0,0,0.4)',
  border: '1px solid #e7eae3',
  // Fits the screen (#1108); `max-h-[92vh] overflow-y-auto` on the panel is the
  // floor for a browser without `dvh`.
  maxHeight: 'calc(100dvh - 2rem)',
} as const;

export interface HomeViewProps {
  /** Opens the generic tournament-creation flow. */
  onCreate: (...args: any[]) => void;
  /** Opens a specific format's setup screen by target key; null when unused. */
  onOpenSetup?: ((...args: any[]) => void) | null;
  /** Opens the player database. */
  onShowPlayers: (...args: any[]) => void;
  /** Opens the tournament archive. */
  onShowArchive: (...args: any[]) => void;
  /** Opens the single-matches view. */
  onShowSingleMatches?: (...args: any[]) => void;
  /** Number of registered single matches. */
  singleMatchesCount: number;
  /** The currently-open tournament, or null when none. */
  tournament?: any;
  /** Re-opens the active tournament. */
  onOpenTournament: (...args: any[]) => void;
  /** Deletes the active tournament without saving. */
  onReset: (...args: any[]) => void;
  /** Archives the active tournament. */
  onArchiveActive: (...args: any[]) => void;
  /** Archived tournaments list. */
  archive: any[];
  /** Whether the archive has finished loading. */
  archiveLoaded: boolean;
  /** Opens an archived tournament. */
  onOpenArchived: (...args: any[]) => void;
  /** Deletes an archived tournament by key. */
  onDeleteArchived: (...args: any[]) => void;
  /** Effective (tenant-scoped) player database. */
  effectivePlayerDB: any[];
  /** Running / paused tournaments. */
  pausedTournaments?: any[];
  /** Resumes a paused tournament by id. */
  onResumePaused: (...args: any[]) => void;
  /** Pauses a running tournament by id; null when unused. */
  onPauseRunning?: ((...args: any[]) => void) | null;
  /** Archives a listed tournament by id; null when unused. */
  onArchiveFromList?: ((...args: any[]) => void) | null;
  /** Deletes a paused tournament by id. */
  onDeletePaused: (...args: any[]) => void;
  /** Deletes all paused tournaments. */
  onDeleteAllPaused: (...args: any[]) => void;
  /** Saved tournament-setup drafts. */
  drafts?: any[];
  /** Opens a draft. */
  onOpenDraft: (...args: any[]) => void;
  /** Deletes a draft. */
  onDeleteDraft: (...args: any[]) => void;
  /** Current club info (name, courts, logo). */
  clubInfo?: any;
  /**
   * The club's booking configuration, for which courts count as bookable
   * (`enabledCourts`, `courtCount`). Absent means the physical count (#873).
   */
  bookingConfig?: any;
  /** The active series (seriespill), or null when none. */
  activeSeries?: any;
  /** Opens the active series. */
  onOpenSeries: (...args: any[]) => void;
  /** Opens the court-booking view. */
  onShowBookings?: (...args: any[]) => void;
  /** Opens the court-booking view on a specific date; null when unused. */
  onShowBookingsOnDate?: ((...args: any[]) => void) | null;
  /** Number of active bookings. */
  bookingsCount?: number;
  /** Opens the activities view. */
  onShowActivities?: (...args: any[]) => void;
  /** Number of open activities. */
  activitiesCount?: number;
  /** Activities list. */
  activities?: any[];
  /** Opens an activity by id. */
  onOpenActivity: (...args: any[]) => void;
  /** Bookings used for the home occupancy / "baner nå" widgets. */
  homeBookings?: any[];
  /** Opens the Banepad central / per-court screen; null when unused. */
  onShowBanepad?: ((...args: any[]) => void) | null;
}

/**
 * Home/landing surface for the club: header, the running + paused tournaments
 * card, active-series banner, drafts, the four big entry cards (tournaments,
 * booking, activities, Banepad) with their side info-pods, the player/archive
 * nav, the "how it works" + ELO explainer grids, and the recent-tournaments
 * history. Extracted verbatim (byte-for-byte body) from the former monolith
 * (legacy/App.jsx). The lone `Padel24Engine.getTournamentType(...)` reference is
 * swapped to the named `getTournamentType` import from the engine barrel (the
 * same function the monolith's Padel24Engine wiring delegated to); all other
 * engine calls were already bare names that resolve to the barrel imports above.
 */
// DEVIATION (lint): `onCreate` and `singleMatchesCount` were part of the monolith's
// signature but unused in the body; renamed to `_`-prefixed locals (keeping the
// public prop names, so the call-site contract is unchanged) to satisfy
// no-unused-vars. Byte-identical otherwise.
export function HomeView({
  onCreate: _onCreate,
  onOpenSetup = null,
  onShowPlayers,
  onShowArchive,
  onShowSingleMatches,
  singleMatchesCount: _singleMatchesCount,
  tournament,
  onOpenTournament,
  onReset,
  onArchiveActive,
  archive,
  archiveLoaded,
  onOpenArchived,
  onDeleteArchived,
  effectivePlayerDB,
  pausedTournaments = [],
  onResumePaused,
  onPauseRunning = null,
  onArchiveFromList = null,
  onDeletePaused,
  onDeleteAllPaused,
  drafts = [],
  onOpenDraft,
  onDeleteDraft,
  clubInfo,
  bookingConfig,
  activeSeries = null,
  onOpenSeries,
  onShowBookings,
  onShowBookingsOnDate = null,
  bookingsCount = 0,
  onShowActivities,
  activitiesCount = 0,
  activities = [],
  onOpenActivity,
  homeBookings = [],
  onShowBanepad = null,
}: HomeViewProps) {
  // `tr`, not `t`: `t` is the tournament / ELO level in the callbacks below.
  const { t: tr, formatDate } = useLocale();
  const weekdayShort = [
    tr('home.weekday.short.sun'),
    tr('home.weekday.short.mon'),
    tr('home.weekday.short.tue'),
    tr('home.weekday.short.wed'),
    tr('home.weekday.short.thu'),
    tr('home.weekday.short.fri'),
    tr('home.weekday.short.sat'),
  ];
  const [confirmDelete, setConfirmDelete] = useState(null);
  // DEVIATION (lint): `activeMenuOpen`/`setActiveMenuOpen` state is declared in the
  // monolith but unused in this range; `_`-prefixed to satisfy no-unused-vars
  // without changing behaviour.
  const [_activeMenuOpen, _setActiveMenuOpen] = useState(false);
  const [confirmDeleteActive, setConfirmDeleteActive] = useState(false);
  // Ids that name and describe the two confirmation dialogs (#900).
  const uid = safeId('home-view', useId());
  const dlg = {
    activeTitle: `${uid}-active-title`,
    activeBody: `${uid}-active-body`,
    archivedTitle: `${uid}-archived-title`,
    archivedBody: `${uid}-archived-body`,
  };
  // Ids that name each entry card's button from its title (#900).
  const card = (k: string) => ({ title: `${uid}-${k}-title`, cta: `${uid}-${k}-cta` });
  const cards = {
    tournaments: card('tournaments'),
    booking: card('booking'),
    activities: card('activities'),
    banepad: card('banepad'),
  };
  // Dark or white text on a colored pill, based on luminance (lime/yellow → dark text).
  const pillText = (hex) => {
    const h = String(hex || '').replace('#', '');
    if (h.length < 6) return '#0f1611';
    const r = parseInt(h.slice(0, 2), 16),
      g = parseInt(h.slice(2, 4), 16),
      b = parseInt(h.slice(4, 6), 16);
    return 0.299 * r + 0.587 * g + 0.114 * b > 150 ? '#0f1611' : '#ffffff';
  };
  return (
    <div className="max-w-7xl mx-auto px-4 md:px-6 py-10 slide-up">
      {/* Confirm deletion of the active tournament */}
      {confirmDeleteActive && (
        // A `Dialog` (#900): named by its title, described by its lead, Tab
        // kept inside, focus given back to the opener. Escape is its «Avbryt»
        // (nothing is deleted), and focus starts there.
        <Dialog
          labelledBy={dlg.activeTitle}
          describedBy={dlg.activeBody}
          onClose={() => setConfirmDeleteActive(false)}
          showClose={false}
          backdropClassName=""
          backdropStyle={CONFIRM_BACKDROP_STYLE}
          className="max-h-[92vh] overflow-y-auto"
          style={CONFIRM_PANEL_STYLE}
        >
          <h3 id={dlg.activeTitle} className="font-display text-lg font-semibold mb-1">
            {tr('home.view.deleteUnsavedTitle')}
          </h3>
          <p id={dlg.activeBody} className="text-sm text-ink-2 mb-4 leading-relaxed">
            {tr('home.view.deleteUnsavedPre')}{' '}
            <strong>{tournament?.config?.name || tr('home.view.theTournament')}</strong>{' '}
            {tr('home.view.deleteUnsavedPost')}
          </p>
          <div className="flex gap-2 justify-end">
            <button
              type="button"
              onClick={() => setConfirmDeleteActive(false)}
              className="btn-ghost"
            >
              {tr('home.common.cancel')}
            </button>
            <button
              type="button"
              onClick={() => {
                onReset();
                setConfirmDeleteActive(false);
              }}
              className="px-4 py-2 rounded-full text-sm font-semibold text-white hover:opacity-90"
              style={{ background: '#f43f5e' }}
            >
              {tr('home.view.deletePermanently')}
            </button>
          </div>
        </Dialog>
      )}
      {/* Header — more compact, less prominent */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-1.5">
          <span className="font-mono text-[10px] font-semibold tracking-[0.15em] uppercase text-ink-3">
            {formatDate(new Date(), {
              weekday: 'long',
              day: 'numeric',
              month: 'long',
            })}
          </span>
          {clubInfo?.name && (
            <>
              <span className="text-ink-4">·</span>
              <span className="font-mono text-[10px] font-semibold tracking-wider uppercase text-ink-2 flex items-center gap-1.5">
                {clubInfo.logoDataUrl ? (
                  <img
                    src={clubInfo.logoDataUrl}
                    alt=""
                    className="w-3.5 h-3.5 rounded-full object-cover"
                  />
                ) : null}
                {clubInfo.name}
              </span>
            </>
          )}
        </div>
        <p className="text-ink-2 text-sm max-w-2xl mt-1">
          {clubInfo?.name
            ? tr('home.view.leadWithClub', { club: clubInfo.name })
            : tr('home.view.leadNoClub', { brand: PORTAL_BRAND.name })}
        </p>
      </div>

      {/* The active tournament is now shown together with the other ongoing ones (in PausedTournamentsCard). */}

      {/* Ongoing + paused tournaments — shown at the TOP, above the series */}
      {(pausedTournaments.length > 0 || (tournament && !tournament.isArchived)) && (
        <PausedTournamentsCard
          pausedTournaments={pausedTournaments}
          activeTournament={tournament && !tournament.isArchived ? tournament : null}
          onOpenActive={onOpenTournament}
          onArchiveActive={onArchiveActive}
          onDeleteActive={() => setConfirmDeleteActive(true)}
          onResume={onResumePaused}
          onPauseRunning={onPauseRunning}
          onArchiveFromList={onArchiveFromList}
          onDelete={onDeletePaused}
          onDeleteAll={onDeleteAllPaused}
        />
      )}

      {/* Active series — lives in its own track, not parked by other tournaments.
          Always shown while the series is active; one click opens it directly. */}
      {activeSeries && (
        <div className="mb-4">
          <button
            onClick={onOpenSeries}
            className="w-full text-left rounded-xl border-2 border-blue-400/40 hover:border-blue-500/60 transition-colors p-4"
            style={{ background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)' }}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="font-mono text-[10px] uppercase tracking-wider text-blue-700 mb-1">
                  {tr('home.view.activeSeries')}
                </div>
                <div className="font-display text-lg font-semibold text-ink-1 truncate">
                  {activeSeries.config?.name || activeSeries.name || tr('home.view.seriesFallback')}
                </div>
                <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                  {(activeSeries.config?.classes || []).map((c) => (
                    <span
                      key={c.id}
                      className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-white/60 text-blue-900"
                    >
                      {tr('home.view.seriesClassDiv', {
                        name: c.name,
                        count: String((c.divisions || []).length),
                      })}
                    </span>
                  ))}
                  <span className="font-mono text-[10px] text-blue-900/70">
                    {tr('home.view.seriesMatches', {
                      done: String((activeSeries.matches || []).filter((m) => m.score).length),
                      total: String((activeSeries.matches || []).length),
                    })}
                  </span>
                  {activeSeries.config?.deadline && (
                    <span className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-800">
                      {tr('home.view.seriesDeadline', { date: activeSeries.config.deadline })}
                    </span>
                  )}
                </div>
              </div>
              <ChevronRight size={20} className="text-blue-600 flex-shrink-0 mt-1" />
            </div>
          </button>
        </div>
      )}

      {/* Started-but-unfinished tournaments (drafts) — not started yet */}
      {drafts.length > 0 && (
        <DraftsCard drafts={drafts} onOpen={onOpenDraft} onDelete={onDeleteDraft} />
      )}

      {/* Main-flow cards: always visible — if there is an active tournament,
          it is automatically parked when the user starts a new one */}
      {/* deviation (lint): the monolith wrapped this block in `{true && (...)}` verbatim;
          eslint-disable keeps the byte-for-byte construct without altering behaviour. */}
      {/* eslint-disable-next-line no-constant-binary-expression */}
      {true && (
        <div className="flex flex-col gap-4">
          {/* ── TOURNAMENTS: main card + its own format box ── */}
          <div className="flex flex-wrap gap-3 items-stretch">
            {/* THE CARD'S BUTTON IS THE KEYBOARD WAY IN (#900). Each entry card
                opens on a click, but it was a <div>, so no keyboard could reach
                it. Its call to action is a real button now, named by the card's
                title; the card keeps its click as a pointer convenience that
                only repeats that button, so it is role="presentation". It is
                not one big <button>: a heading and paragraphs inside a button
                are invalid nesting and lose their semantics. The same holds
                for the booking, activities and Banepad cards below. */}
            <div
              role="presentation"
              onClick={() => onShowSingleMatches?.()}
              className="card-elev text-left cursor-pointer hover:shadow-lg transition-all flex overflow-hidden"
              style={{
                background: 'linear-gradient(135deg, #fef3c7 0%, #fbbf24 55%, #f59e0b 100%)',
                minHeight: 180,
                flex: '1 1 300px',
                minWidth: 0,
              }}
            >
              <div
                className="flex items-center justify-center flex-shrink-0"
                style={{ width: 120, alignSelf: 'stretch', background: 'rgba(255,255,255,0.28)' }}
              >
                <Trophy size={52} strokeWidth={2} />
              </div>
              <div className="flex flex-col justify-center p-5 flex-1 min-w-0">
                <span className="font-mono text-[13px] font-extrabold tracking-[0.16em] uppercase mb-1.5">
                  {tr('home.view.tournamentsEyebrow')}
                </span>
                <h2
                  id={cards.tournaments.title}
                  className="font-display text-xl md:text-2xl font-semibold mb-1 leading-tight"
                >
                  {tr('home.view.createTournament')}
                </h2>
                <p className="text-ink-2 text-[13px] leading-snug">
                  {tr('home.view.tournamentsLead')}
                </p>
                <button
                  type="button"
                  id={cards.tournaments.cta}
                  aria-labelledby={`${cards.tournaments.title} ${cards.tournaments.cta}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onShowSingleMatches?.();
                  }}
                  className="self-start inline-flex items-center gap-1.5 text-xs font-bold mt-2 rounded focus-ring"
                  style={{ color: '#7c2d12' }}
                >
                  {tr('home.view.seeAllFormats')} <ChevronRight size={14} />
                </button>
              </div>
            </div>
            <div
              className="card-elev p-3.5 flex flex-col justify-center home-info-pod"
              style={{
                flex: '0 0 240px',
                background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
              }}
            >
              <div
                className="font-mono text-[9px] uppercase tracking-wider font-bold mb-2"
                style={{ color: '#b45309' }}
              >
                {tr('home.view.pickFormat')}
              </div>
              <div className="flex flex-col gap-1.5">
                {[
                  {
                    label: TOURNAMENT_TYPES.padel24?.label,
                    color: TOURNAMENT_TYPES.padel24?.color,
                    target: 'wizard',
                  },
                  {
                    label: TOURNAMENT_TYPES.klubbmesterskap?.label,
                    color: '#f9a8d4',
                    target: 'klubbmesterskapSetup',
                  },
                  {
                    label: tr('home.view.miniTournaments'),
                    color: '#fde047',
                    target: 'miniTournaments',
                  },
                ].map((row) => (
                  <button
                    key={row.target}
                    onClick={(e) => {
                      e.stopPropagation();
                      onOpenSetup?.(row.target);
                    }}
                    className="w-full font-mono text-[11px] font-bold px-3 py-2 rounded-full text-center hover:brightness-105 transition-all"
                    style={{
                      background: row.color,
                      color: pillText(row.color),
                      boxShadow: '0 1px 2px rgba(0,0,0,0.18)',
                    }}
                  >
                    {row.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* ── COURT BOOKING: main card + its own availability box ── */}
          {onShowBookings && (
            <div className="flex flex-wrap gap-3 items-stretch">
              <div
                role="presentation"
                onClick={() => onShowBookings?.()}
                className="card-elev text-left cursor-pointer hover:shadow-lg transition-all flex overflow-hidden"
                style={{
                  background: 'linear-gradient(135deg, #d1fae5 0%, #34d399 60%, #059669 100%)',
                  minHeight: 180,
                  flex: '1 1 300px',
                  minWidth: 0,
                }}
              >
                <div
                  className="flex items-center justify-center flex-shrink-0"
                  style={{ width: 120, alignSelf: 'stretch', background: 'rgba(255,255,255,0.28)' }}
                >
                  <Calendar size={50} strokeWidth={2} style={{ color: '#064e3b' }} />
                </div>
                <div className="flex flex-col justify-center p-5 flex-1 min-w-0">
                  <span
                    className="font-mono text-[13px] font-extrabold tracking-[0.16em] uppercase mb-1.5"
                    style={{ color: '#064e3b' }}
                  >
                    {tr('home.view.bookingEyebrow')}
                  </span>
                  <h3
                    id={cards.booking.title}
                    className="font-display text-xl md:text-2xl font-semibold mb-1 leading-tight"
                    style={{ color: '#022c22' }}
                  >
                    {tr('home.view.reserveCourt')}
                  </h3>
                  <p className="text-[13px] leading-snug" style={{ color: '#064e3b' }}>
                    {tr('home.view.bookingLead')}
                    {bookingsCount > 0 &&
                      tr(
                        bookingsCount === 1
                          ? 'home.view.bookingsActiveOne'
                          : 'home.view.bookingsActiveMany',
                        { count: String(bookingsCount) },
                      )}
                  </p>
                  <button
                    type="button"
                    id={cards.booking.cta}
                    aria-labelledby={`${cards.booking.title} ${cards.booking.cta}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      onShowBookings?.();
                    }}
                    className="self-start inline-flex items-center gap-1.5 text-xs font-bold mt-2 rounded focus-ring"
                    style={{ color: '#022c22' }}
                  >
                    {tr('home.view.open')} <ChevronRight size={14} />
                  </button>
                </div>
              </div>
              {(() => {
                // THE CALENDAR'S COURTS (#873): `enabledCourts`, then
                // `courtCount`, then the centre's count, or four, exactly as
                // BookingView's own day indicator counts them, and bookings on
                // any other court left out as it leaves them out. It used to be
                // `clubInfo.courts || 4`, so a club booking four of a centre's
                // five courts was shown a fifth court's worth of free time.
                const bookable = clubEffectiveCourts(bookingConfig, clubInfo);
                const courts = Math.max(1, bookable.length);
                const OPEN_MIN = 7 * 60,
                  CLOSE_MIN = 23 * 60; // assumed opening hours 07–23
                const span = CLOSE_MIN - OPEN_MIN;
                const segB = [OPEN_MIN, OPEN_MIN + span / 3, OPEN_MIN + (2 * span) / 3, CLOSE_MIN];
                const toMin = (t) => {
                  const [h, m] = String(t || '0:0')
                    .split(':')
                    .map(Number);
                  return (h || 0) * 60 + (m || 0);
                };
                const dotHex = (r) =>
                  r == null
                    ? '#cbd2cb'
                    : r > 0.8
                      ? '#16a34a'
                      : r > 0.6
                        ? '#65a30d'
                        : r > 0.4
                          ? '#eab308'
                          : r > 0.2
                            ? '#f97316'
                            : '#dc2626';
                const now = new Date();
                const nowMin = now.getHours() * 60 + now.getMinutes();
                const base = new Date();
                base.setHours(0, 0, 0, 0);
                const days = [];
                for (let i = 0; i < 7; i++) {
                  const d = new Date(base);
                  d.setDate(base.getDate() + i);
                  const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
                  const dayB = (homeBookings || []).filter(
                    (b) => b.date === iso && bookable.includes(b.court),
                  );
                  const isToday = i === 0;
                  const segs = [0, 1, 2].map((si) => {
                    let segStart = segB[si];
                    const segEnd = segB[si + 1];
                    if (isToday) {
                      if (segEnd <= nowMin) return null;
                      if (segStart < nowMin) segStart = nowMin;
                    }
                    const slots = Math.max(1, Math.round((segEnd - segStart) / 30));
                    const cap = courts * slots;
                    let busy = 0;
                    dayB.forEach((b) => {
                      const bs = toMin(b.startTime);
                      const be = b.endTime
                        ? toMin(b.endTime)
                        : b.durationMin
                          ? bs + Number(b.durationMin)
                          : bs + 60;
                      const s = Math.max(bs, segStart),
                        e = Math.min(be, segEnd);
                      if (e > s) busy += Math.round((e - s) / 30);
                    });
                    return cap > 0 ? Math.max(0, (cap - busy) / cap) : 1;
                  });
                  const wd = weekdayShort[d.getDay()];
                  days.push({ iso, label: i === 0 ? tr('home.view.today') : wd, segs });
                }
                return (
                  <div
                    className="card-elev p-3 flex flex-col justify-center home-info-pod"
                    style={{
                      flex: '0 0 240px',
                      background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
                    }}
                  >
                    <div
                      className="font-mono text-[9px] uppercase tracking-wider font-bold mb-1.5"
                      style={{ color: '#047857' }}
                    >
                      {tr('home.view.availability7')}
                    </div>
                    <div className="flex flex-col gap-0.5">
                      {days.map((d) => (
                        <button
                          key={d.iso}
                          onClick={(e) => {
                            e.stopPropagation();
                            (onShowBookingsOnDate || onShowBookings)?.(d.iso);
                          }}
                          className="flex items-center justify-between gap-2 rounded px-2 py-1 hover:bg-bg-soft transition-colors"
                          title={tr('home.view.dayDotsTitle', { day: d.label })}
                        >
                          <span className="font-mono text-[9px] font-bold uppercase text-ink-2">
                            {d.label}
                          </span>
                          <span className="flex items-center gap-1 flex-shrink-0">
                            <span
                              className="rounded-full"
                              style={{ width: 6, height: 6, background: dotHex(d.segs[0]) }}
                            />
                            <span
                              className="rounded-full"
                              style={{ width: 6, height: 6, background: dotHex(d.segs[1]) }}
                            />
                            <span
                              className="rounded-full"
                              style={{ width: 6, height: 6, background: dotHex(d.segs[2]) }}
                            />
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </div>
          )}

          {/* ── ACTIVITIES: main card + its own "nearest" box ── */}
          {onShowActivities && (
            <div className="flex flex-wrap gap-3 items-stretch">
              <div
                role="presentation"
                onClick={() => onShowActivities?.()}
                className="card-elev text-left cursor-pointer hover:shadow-lg transition-all flex overflow-hidden"
                style={{
                  background: 'linear-gradient(135deg, #ede9fe 0%, #c4b5fd 60%, #8b5cf6 100%)',
                  minHeight: 180,
                  flex: '1 1 300px',
                  minWidth: 0,
                }}
              >
                <div
                  className="flex items-center justify-center flex-shrink-0"
                  style={{ width: 120, alignSelf: 'stretch', background: 'rgba(255,255,255,0.28)' }}
                >
                  <img
                    src={STARPOINT_ICON_URI}
                    alt=""
                    style={{ height: 60, width: 'auto', display: 'block' }}
                    draggable={false}
                    className="select-none flex-shrink-0"
                  />
                </div>
                <div className="flex flex-col justify-center p-5 flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1.5">
                    <span
                      className="font-mono text-[13px] font-extrabold tracking-[0.16em] uppercase"
                      style={{ color: '#4c1d95' }}
                    >
                      {tr('home.view.activitiesEyebrow')}
                    </span>
                    {activitiesCount > 0 && (
                      <span
                        className="font-mono text-[10px] font-bold px-2 py-0.5 rounded-full"
                        style={{ background: '#2e1065', color: '#ede9fe' }}
                      >
                        {tr('home.view.activitiesOpenCount', { count: String(activitiesCount) })}
                      </span>
                    )}
                  </div>
                  <h3
                    id={cards.activities.title}
                    className="font-display text-xl md:text-2xl font-semibold mb-1 leading-tight"
                    style={{ color: '#2e1065' }}
                  >
                    {tr('home.view.signUp')}
                  </h3>
                  <p className="text-[13px] leading-snug" style={{ color: '#4c1d95' }}>
                    {tr('home.view.activitiesLead')}
                  </p>
                  <button
                    type="button"
                    id={cards.activities.cta}
                    aria-labelledby={`${cards.activities.title} ${cards.activities.cta}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      onShowActivities?.();
                    }}
                    className="self-start inline-flex items-center gap-1.5 text-xs font-bold mt-2 rounded focus-ring"
                    style={{ color: '#2e1065' }}
                  >
                    {tr('home.view.open')} <ChevronRight size={14} />
                  </button>
                </div>
              </div>
              {(() => {
                const actStart = (a) => {
                  const mine = (homeBookings || []).filter((b) => b.seriesId === a.id);
                  const stamps = mine
                    .map((b) => new Date(`${b.date}T${b.startTime || '00:00'}:00`).getTime())
                    .filter((n) => !isNaN(n));
                  let ms = stamps.length ? Math.min(...stamps) : Infinity;
                  if (ms === Infinity && a.bookingDraft?.slotMode === 'standalone') {
                    const r = (a.bookingDraft.slotRooms || [])[0];
                    if (r?.singleDate) {
                      const t = new Date(`${r.singleDate}T${r.startTime || '00:00'}:00`).getTime();
                      if (!isNaN(t)) ms = t;
                    }
                  }
                  if (ms === Infinity && a.config?.activityDate) {
                    const t = new Date(
                      `${a.config.activityDate}T${a.config.activityTime || '00:00'}:00`,
                    ).getTime();
                    if (!isNaN(t)) ms = t;
                  }
                  return ms;
                };
                const fmtWhen = (ms) => {
                  if (ms === Infinity) return tr('home.view.dateNotSet');
                  const d = new Date(ms);
                  const wd = weekdayShort[d.getDay()];
                  const mo = [
                    tr('home.month.short.jan'),
                    tr('home.month.short.feb'),
                    tr('home.month.short.mar'),
                    tr('home.month.short.apr'),
                    tr('home.month.short.may'),
                    tr('home.month.short.jun'),
                    tr('home.month.short.jul'),
                    tr('home.month.short.aug'),
                    tr('home.month.short.sep'),
                    tr('home.month.short.oct'),
                    tr('home.month.short.nov'),
                    tr('home.month.short.dec'),
                  ][d.getMonth()];
                  const hh = String(d.getHours()).padStart(2, '0'),
                    mm = String(d.getMinutes()).padStart(2, '0');
                  const when = tr('home.view.whenDate', {
                    wd,
                    day: String(d.getDate()),
                    mo,
                  });
                  return hh !== '00' || mm !== '00'
                    ? when + tr('home.view.whenTime', { hh, mm })
                    : when;
                };
                const withMs = (activities || [])
                  .filter((a) => a.status !== 'cancelled')
                  .map((a) => ({ a, ms: actStart(a) }));
                const future = withMs
                  .filter((x) => x.ms !== Infinity && x.ms >= Date.now() - 6 * 3600000)
                  .sort((x, y) => x.ms - y.ms);
                const list = (future.length ? future : withMs.sort((x, y) => x.ms - y.ms)).slice(
                  0,
                  3,
                );
                return (
                  <div
                    className="card-elev p-3.5 flex flex-col justify-center home-info-pod"
                    style={{
                      flex: '0 0 240px',
                      background: 'linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)',
                    }}
                  >
                    <div
                      className="font-mono text-[9px] uppercase tracking-wider font-bold mb-2"
                      style={{ color: '#6d28d9' }}
                    >
                      {tr('home.view.nearestActivities')}
                    </div>
                    {list.length === 0 ? (
                      <div className="text-[11px] py-1 text-ink-3">
                        {tr('home.view.noUpcoming')}
                      </div>
                    ) : (
                      <div className="flex flex-col">
                        {list.map(({ a, ms }, idx) => (
                          <button
                            key={a.id}
                            onClick={(e) => {
                              e.stopPropagation();
                              onOpenActivity?.(a.id);
                            }}
                            className={`w-full text-left py-1.5 hover:opacity-70 transition-opacity ${idx > 0 ? 'border-t border-line' : ''}`}
                          >
                            <div className="text-[12px] font-semibold truncate leading-snug text-ink-1">
                              {a.config?.name || tr('home.view.activityFallback')}
                            </div>
                            <div
                              className="font-mono text-[10px] leading-snug mt-0.5"
                              style={{ color: '#7c3aed' }}
                            >
                              {fmtWhen(ms)}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          )}

          {/* ── BANEPAD: main card + its own "courts now" box ── */}
          {onShowBanepad && (
            <div className="flex flex-wrap gap-3 items-stretch">
              <div
                role="presentation"
                onClick={() => onShowBanepad?.()}
                className="card-elev text-left cursor-pointer hover:shadow-lg transition-all flex overflow-hidden"
                style={{
                  background: 'linear-gradient(135deg, #a5f3fc 0%, #22d3ee 60%, #0891b2 100%)',
                  minHeight: 180,
                  flex: '1 1 300px',
                  minWidth: 0,
                }}
              >
                <div
                  className="flex items-center justify-center flex-shrink-0"
                  style={{ width: 120, alignSelf: 'stretch', background: 'rgba(255,255,255,0.28)' }}
                >
                  <Tablet size={50} strokeWidth={2} style={{ color: '#083344' }} />
                </div>
                <div className="flex flex-col justify-center p-5 flex-1 min-w-0">
                  <span
                    className="font-mono text-[13px] font-extrabold tracking-[0.16em] uppercase mb-1.5"
                    style={{ color: '#083344' }}
                  >
                    {tr('home.view.banepadEyebrow')}
                  </span>
                  <h3
                    id={cards.banepad.title}
                    className="font-display text-xl md:text-2xl font-semibold mb-1 leading-tight"
                    style={{ color: '#062c33' }}
                  >
                    {tr('home.view.screensAtCourts')}
                  </h3>
                  <p className="text-[13px] leading-snug" style={{ color: '#083344' }}>
                    {tr('home.view.banepadLead')}
                  </p>
                  <button
                    type="button"
                    id={cards.banepad.cta}
                    aria-labelledby={`${cards.banepad.title} ${cards.banepad.cta}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      onShowBanepad?.();
                    }}
                    className="self-start inline-flex items-center gap-1.5 text-xs font-bold mt-2 rounded focus-ring"
                    style={{ color: '#062c33' }}
                  >
                    {tr('home.view.openCentral')} <ChevronRight size={14} />
                  </button>
                </div>
              </div>
              {(() => {
                // Sentralen's list, with the booking config it uses (#873).
                const courts = banepadCourtList(bookingConfig ?? null, clubInfo);
                const now = new Date();
                const nowMin = now.getHours() * 60 + now.getMinutes();
                const todayISO = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
                const rows = courts.slice(0, 8).map((c) => ({
                  c,
                  r: banepadResolveCourt({
                    court: c,
                    bookings: homeBookings,
                    tournament,
                    activeSeries,
                    nowMin,
                    todayISO,
                  }),
                }));
                const dot = (mode, live) =>
                  mode === 'tournament'
                    ? live
                      ? '#65a30d'
                      : '#a3a3a3'
                    : mode === 'booking'
                      ? '#06b6d4'
                      : '#cbd5e1';
                const busy = rows.filter(
                  (x) => x.r.mode === 'booking' || (x.r.mode === 'tournament' && x.r.current),
                ).length;
                return (
                  <div
                    className="card-elev p-3 flex flex-col justify-center home-info-pod"
                    style={{
                      flex: '0 0 240px',
                      background: 'linear-gradient(135deg, #ecfeff 0%, #cffafe 100%)',
                    }}
                  >
                    <div
                      className="font-mono text-[9px] uppercase tracking-wider font-bold mb-1.5 flex items-center justify-between"
                      style={{ color: '#0e7490' }}
                    >
                      <span>{tr('home.view.courtsNow')}</span>
                      <span>
                        {busy}/{courts.length}
                      </span>
                    </div>
                    <div className="flex flex-col gap-0.5">
                      {rows.map(({ c, r }) => (
                        <button
                          key={c}
                          onClick={(e) => {
                            e.stopPropagation();
                            onShowBanepad?.();
                          }}
                          className="flex items-center justify-between gap-2 rounded px-2 py-1 hover:bg-bg-soft transition-colors"
                        >
                          <span className="font-mono text-[10px] font-bold uppercase text-ink-2 truncate">
                            {tr('home.view.court', { court: String(c) })}
                          </span>
                          <span className="flex items-center gap-1.5 flex-shrink-0">
                            <span className="font-mono text-[9px]" style={{ color: '#0e7490' }}>
                              {r.mode === 'tournament'
                                ? r.current
                                  ? tr('home.view.live')
                                  : tr('home.view.next')
                                : r.mode === 'booking'
                                  ? tr('home.view.booked')
                                  : tr('home.view.courtAvailable')}
                            </span>
                            <span
                              className="rounded-full"
                              style={{ width: 7, height: 7, background: dot(r.mode, !!r.current) }}
                            />
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </div>
          )}
        </div>
      )}

      {/* The "Waiting for sign-up" list has been moved to the Activities module.
          The home page only shows the Activities banner with the number of open ones. */}

      {/* Navigation — Player database + Archive */}
      <div className="mt-6 grid sm:grid-cols-2 gap-3">
        <button
          onClick={onShowPlayers}
          className="card p-5 text-left hover:shadow-md transition-all hover:-translate-y-0.5 group flex items-center justify-between"
        >
          <div>
            <div className="flex items-center gap-2 mb-1.5">
              <Users size={14} strokeWidth={2.5} className="text-ink-2" />
              <span className="font-mono text-[10px] font-bold tracking-[0.18em] uppercase text-ink-3">
                {tr('home.view.databaseEyebrow')}
              </span>
            </div>
            <h3 className="font-display text-base font-semibold mb-0.5 leading-tight">
              {tr('home.view.playerDatabase')}
            </h3>
            <p className="text-[11px] text-ink-3 leading-tight">
              {effectivePlayerDB
                ? tr('home.view.playersCount', { count: String(effectivePlayerDB.length) })
                : tr('home.view.playersFallback')}
            </p>
          </div>
          <ChevronRight
            size={16}
            className="text-ink-3 flex-shrink-0 group-hover:translate-x-0.5 transition-transform"
          />
        </button>

        <button
          onClick={onShowArchive}
          className="card p-5 text-left hover:shadow-md transition-all hover:-translate-y-0.5 group flex items-center justify-between"
        >
          <div>
            <div className="flex items-center gap-2 mb-1.5">
              <Trophy size={14} strokeWidth={2.5} className="text-ink-2" />
              <span className="font-mono text-[10px] font-bold tracking-[0.18em] uppercase text-ink-3">
                {tr('home.view.archiveEyebrow')}
              </span>
            </div>
            <h3 className="font-display text-base font-semibold mb-0.5 leading-tight">
              {tr('home.view.tournamentArchive')}
            </h3>
            <p className="text-[11px] text-ink-3 leading-tight">
              {archiveLoaded
                ? tr(
                    archive.length === 1
                      ? 'home.view.archiveSummaryOne'
                      : 'home.view.archiveSummaryMany',
                    {
                      count: String(archive.length),
                      matches: String(
                        archive.reduce((s, it) => s + (it.data?.matches?.length || 0), 0),
                      ),
                    },
                  )
                : tr('home.view.loading')}
            </p>
          </div>
          <ChevronRight
            size={16}
            className="text-ink-3 flex-shrink-0 group-hover:translate-x-0.5 transition-transform"
          />
        </button>
      </div>

      {/* Info grid */}
      <div className="mt-6 grid sm:grid-cols-2 gap-3">
        <div className="card p-5">
          <div className="font-mono text-[10px] font-semibold tracking-wider uppercase text-ink-3 mb-3">
            {tr('home.view.howItWorks')}
          </div>
          <ol className="space-y-2 text-sm text-ink-2">
            {[
              tr('home.view.step1'),
              tr('home.view.step2'),
              tr('home.view.step3'),
              tr('home.view.step4'),
              tr('home.view.step5'),
            ].map((step, i) => (
              <li key={i} className="flex gap-2.5 items-start">
                <span className="font-mono text-[10px] font-bold text-ink-3 bg-bg-soft rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                  {i + 1}
                </span>
                <span>{step}</span>
              </li>
            ))}
          </ol>
        </div>
        <div className="card p-5">
          <div className="font-mono text-[10px] font-semibold tracking-wider uppercase text-ink-3 mb-3">
            {tr('home.view.features')}
          </div>
          <ul className="space-y-2 text-sm text-ink-2">
            {[
              tr('home.view.feature1'),
              tr('home.view.feature2'),
              tr('home.view.feature3'),
              tr('home.view.feature4'),
              tr('home.view.feature5'),
            ].map((line, i) => (
              <li key={i} className="flex gap-2 items-start">
                <span className="text-lime-deep font-bold text-xs mt-0.5">✓</span>
                <span>{line}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* ELO explainer */}
      <div className="card p-5 mt-3">
        <div className="flex items-start gap-3 mb-3">
          <div
            className="font-mono text-xs font-bold bg-ink rounded px-2 py-1 flex-shrink-0"
            style={{ color: '#c2f02f' }}
          >
            ELO
          </div>
          <div>
            <h3 className="font-display text-base font-semibold mb-1">
              {tr('home.view.whatIsElo')}
            </h3>
            <p className="text-sm text-ink-2 leading-relaxed">
              {tr('home.view.eloExplainerPre')} <strong>{tr('home.view.eloAverage')}</strong>{' '}
              {tr('home.view.eloExplainerPost')}
            </p>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-1.5 text-xs">
          {ELO_LEVELS.map((t) => (
            <div
              key={t.id}
              className="rounded-lg p-2 border border-line text-left"
              style={{ background: t.color }}
              title={t.description}
            >
              <div className="font-mono text-[9px] font-bold opacity-70" style={{ color: t.text }}>
                {tr('home.view.eloLevel', { level: String(t.level) })}
              </div>
              <div className="text-[11px] font-bold leading-tight" style={{ color: t.text }}>
                {t.name}
              </div>
              <div className="font-mono text-[9px] opacity-80 mt-0.5" style={{ color: t.text }}>
                {t.max >= 9999 ? `${t.min}+` : `${t.min}–${t.max - 1}`}
              </div>
            </div>
          ))}
        </div>
        <p className="text-[11px] text-ink-3 mt-3 leading-relaxed">{tr('home.view.eloScale')}</p>
      </div>

      {/* Confirm deletion of an archived tournament */}
      {confirmDelete && (
        // A `Dialog` (#900), as the one above: Escape is «Avbryt», focus starts
        // there, and goes back to the row's delete button on close.
        <Dialog
          labelledBy={dlg.archivedTitle}
          describedBy={dlg.archivedBody}
          onClose={() => setConfirmDelete(null)}
          showClose={false}
          backdropClassName=""
          backdropStyle={CONFIRM_BACKDROP_STYLE}
          className="max-h-[92vh] overflow-y-auto"
          style={CONFIRM_PANEL_STYLE}
        >
          <h3 id={dlg.archivedTitle} className="font-display text-lg font-semibold mb-1">
            {tr('home.view.deleteTournamentTitle')}
          </h3>
          <p id={dlg.archivedBody} className="text-sm text-ink-2 mb-4 leading-relaxed">
            {tr('home.view.deleteArchivedPre')}{' '}
            <strong>
              {archive.find((item) => item.key === confirmDelete)?.data?.config?.name ||
                tr('home.view.thisTournament')}
            </strong>
            {tr('home.view.deleteArchivedPost')}
          </p>
          <div className="flex gap-2 justify-end">
            <button type="button" onClick={() => setConfirmDelete(null)} className="btn-ghost">
              {tr('home.common.cancel')}
            </button>
            <button
              type="button"
              onClick={() => {
                onDeleteArchived(confirmDelete);
                setConfirmDelete(null);
              }}
              className="px-4 py-2 rounded-full text-sm font-semibold text-white hover:opacity-90"
              style={{ background: '#f43f5e' }}
            >
              {tr('home.view.yesDeletePermanently')}
            </button>
          </div>
        </Dialog>
      )}

      {/* Tournament history — last 3 */}
      {archiveLoaded && archive.length > 0 && (
        <div className="mt-8">
          <div className="flex items-baseline justify-between mb-3">
            <h2 className="font-display text-xl font-semibold">
              {tr('home.view.recentTournaments')}
            </h2>
            <button
              onClick={onShowArchive}
              className="text-xs font-mono text-ink-2 hover:text-ink flex items-center gap-1 hover:underline"
            >
              {tr('home.view.seeAll', { count: String(archive.length) })} <ChevronRight size={12} />
            </button>
          </div>
          <div className="space-y-2">
            {archive.slice(0, 3).map((item) => {
              const t = item.data;
              // Fetch all winners — can be 2 (winning team) or more in case of
              // a shared 1st place (Americano with winnerSource='standings').
              const winners = getTournamentWinners(t);
              const date = t.archivedAt ? new Date(t.archivedAt) : null;
              const dateLabel = date
                ? formatDate(date, {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                  })
                : tr('home.view.unknownDate');
              const playerCount = t.players?.length || 0;
              const matchCount = t.matches?.length || 0;

              return (
                <div
                  key={item.key}
                  className="card-elev p-4 hover:shadow-md transition-all flex items-center gap-3"
                >
                  <div
                    onClick={() => onOpenArchived(t)}
                    className="flex-1 min-w-0 cursor-pointer"
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') onOpenArchived(t);
                    }}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      {(() => {
                        // deviation: Padel24Engine.getTournamentType -> getTournamentType named import (identical binding)
                        const tt = getTournamentType(t.config);
                        return (
                          <span
                            className="pill text-[10px] font-mono font-bold"
                            style={{ background: tt.color + '40', color: '#0f1611' }}
                            title={tt.label}
                          >
                            {tt.abbrev}
                          </span>
                        );
                      })()}
                      <span className="pill bg-bg-soft text-ink-2 text-[10px]">
                        <Trophy size={10} className="inline mr-1" />
                        {tr('home.view.done')}
                      </span>
                      <span className="font-mono text-[10px] text-ink-3">{dateLabel}</span>
                    </div>
                    <div className="font-display text-base font-semibold leading-tight mb-0.5 truncate">
                      {t.config?.name || tr('home.view.tournamentFallback')}
                    </div>
                    <div className="text-xs text-ink-3 truncate">
                      {t.isVirtual
                        ? tr('home.view.matchesRegistered', { count: String(matchCount) })
                        : tr('home.view.playersAndMatches', {
                            players: String(playerCount),
                            matches: String(matchCount),
                          })}
                      {winners && winners.winners.length > 0 && !t.isVirtual && (
                        <> · 🏆 {winners.winners.map((p) => p.name).join(' & ')}</>
                      )}
                    </div>
                  </div>

                  {!t.isVirtual && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setConfirmDelete(item.key);
                      }}
                      className="flex-shrink-0 p-2 rounded-full text-ink-3 hover:bg-rose-soft hover:text-rose transition-colors"
                      title={tr('home.view.deleteTournamentAria')}
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
